/**
 * QuotaWarningModal - 90% threshold urgent modal (Updated with Tier-Aware Messaging)
 *
 * Features:
 * - Full-screen modal with dark overlay
 * - Red urgent styling
 * - Requires explicit dismissal (no auto-dismiss)
 * - Tier-specific CTAs and messaging
 * - Analytics tracking with tier context
 *
 * Tier-Specific Behavior:
 * - Anonymous: "Sign Up Free" → Free tier (10/day vs 10/week for guests)
 * - Free: "Upgrade to Drama+" → 500/day + archetypes
 * - Drama+: "Upgrade to DramaX" → 1000/day + transformation
 * - DramaX: "Contact Support" → Enterprise options
 * - Family Member: "Message Admin" → Coordinate with family
 *
 * Anonymous Quota: 10 analyses per WEEK (resets Monday 00:00 UTC)
 *
 * Created: 2025-11-03
 * Updated: 2025-11-25 (Tier-aware messaging)
 */
import { View, Text, Pressable, Modal } from 'react-native';
import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '../../lib/analytics';

/** Supported subscription tiers for modal messaging */
export type QuotaWarningTier =
  | 'anonymous'
  | 'free'
  | 'drama_plus'
  | 'dramax'
  | 'family_member';

interface QuotaWarningModalProps {
  visible: boolean;
  dailyAnalysesUsed: number;
  dailyAnalysesLimit: number;
  currentTier: QuotaWarningTier;
  /** For family_member tier: number of family members sharing quota */
  familyMemberCount?: number;
  onUpgrade?: () => void;
  onDismiss: () => void;
}

export function QuotaWarningModal({
  visible,
  dailyAnalysesUsed,
  dailyAnalysesLimit,
  currentTier,
  familyMemberCount,
  onUpgrade,
  onDismiss,
}: QuotaWarningModalProps) {
  const { t } = useTranslation('subscription');
  const [hasTrackedShow, setHasTrackedShow] = useState(false);

  // Memoize tier-specific content
  const tierContent = useMemo(() => {
    const tierKey = `quota.warning.modal.by_tier.${currentTier}`;

    // Try tier-specific keys first, fall back to legacy
    const title =
      t(`${tierKey}.title` as never) || t('quota.warning.modal.title');
    const body =
      t(`${tierKey}.body` as never) ||
      (currentTier === 'anonymous'
        ? t('quota.warning.modal.description')
        : t('quota.warning.modal.description_authenticated'));
    const ctaPrimary =
      t(`${tierKey}.cta_primary` as never) ||
      t('quota.warning.modal.cta_upgrade');
    const ctaSecondary =
      t(`${tierKey}.cta_secondary` as never) ||
      t('quota.warning.modal.cta_later');

    // Benefits - support interpolation for family_member tier
    const benefitOptions =
      currentTier === 'family_member' && familyMemberCount
        ? { memberCount: familyMemberCount }
        : {};

    const benefits = [
      t(`${tierKey}.benefits.item1` as never, benefitOptions) ||
        t('quota.warning.modal.benefits.unlimited'),
      t(`${tierKey}.benefits.item2` as never, benefitOptions) ||
        t('quota.warning.modal.benefits.priority_support'),
      t(`${tierKey}.benefits.item3` as never, benefitOptions) ||
        t('quota.warning.modal.benefits.advanced_analytics'),
    ];

    return { title, body, ctaPrimary, ctaSecondary, benefits };
  }, [currentTier, t, familyMemberCount]);

  // Track modal shown
  useEffect(() => {
    if (visible && !hasTrackedShow) {
      captureEvent('quota_warning_modal_shown', {
        current_tier: currentTier,
        analyses_used: dailyAnalysesUsed,
        analyses_limit: dailyAnalysesLimit,
        usage_percentage: Math.round(
          (dailyAnalysesUsed / dailyAnalysesLimit) * 100
        ),
      });
      setHasTrackedShow(true);
    }
  }, [
    visible,
    hasTrackedShow,
    currentTier,
    dailyAnalysesUsed,
    dailyAnalysesLimit,
  ]);

  const handleUpgrade = () => {
    captureEvent('quota_warning_cta_clicked', {
      current_tier: currentTier,
      cta_type: 'primary',
      target_action:
        currentTier === 'anonymous'
          ? 'signup'
          : currentTier === 'dramax'
            ? 'contact_support'
            : currentTier === 'family_member'
              ? 'message_admin'
              : 'upgrade',
    });
    onUpgrade?.();
    onDismiss();
  };

  const handleDismiss = () => {
    captureEvent('quota_warning_dismissed', {
      current_tier: currentTier,
      analyses_used: dailyAnalysesUsed,
    });
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleDismiss}
      testID="quota-warning-modal"
    >
      {/* Dark overlay - use inline style for web compatibility */}
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        {/* Modal content */}
        <View
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: 24,
            width: '100%',
            maxWidth: 400,
            borderWidth: 2,
            borderColor: '#EF4444', // red-500
          }}
        >
          {/* Icon and title - Tier-aware */}
          <View style={{ alignItems: 'center', marginBottom: 16 }}>
            <Text style={{ fontSize: 48, marginBottom: 12 }}>🚨</Text>
            <Text
              style={{
                fontSize: 20,
                fontWeight: '700',
                color: '#7F1D1D', // red-900
                textAlign: 'center',
              }}
            >
              {tierContent.title}
            </Text>
          </View>

          {/* Message - Tier-aware */}
          <Text
            style={{
              fontSize: 14,
              color: '#374151', // gray-700
              textAlign: 'center',
              marginBottom: 8,
            }}
          >
            {tierContent.body}
          </Text>
          <Text
            style={{
              fontSize: 12,
              color: '#4B5563', // gray-600
              textAlign: 'center',
              marginBottom: 24,
            }}
          >
            ({dailyAnalysesUsed} of {dailyAnalysesLimit} used)
          </Text>

          {/* Benefits list - Tier-aware */}
          <View
            style={{
              backgroundColor: '#F9FAFB', // gray-50
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: '500',
                color: '#111827', // gray-900
                marginBottom: 8,
              }}
            >
              {t('quota.warning.modal.upgrade_benefits')}
            </Text>
            <View style={{ gap: 8 }}>
              {tierContent.benefits.map((benefit, index) => (
                <View
                  key={index}
                  style={{ flexDirection: 'row', alignItems: 'center' }}
                >
                  <Text style={{ color: '#16A34A', marginRight: 8 }}>✓</Text>
                  <Text style={{ fontSize: 14, color: '#374151' }}>
                    {benefit}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Primary CTA: Tier-aware */}
          {onUpgrade && (
            <Pressable
              onPress={handleUpgrade}
              style={{
                backgroundColor: '#DC2626', // red-600
                paddingVertical: 16,
                paddingHorizontal: 24,
                borderRadius: 8,
                marginBottom: 12,
                minHeight: 44,
              }}
              accessibilityRole="button"
              accessibilityLabel={tierContent.ctaPrimary}
              testID="warning-primary-button"
            >
              <Text
                style={{
                  color: '#ffffff',
                  fontSize: 16,
                  fontWeight: '600',
                  textAlign: 'center',
                }}
              >
                {tierContent.ctaPrimary}
              </Text>
            </Pressable>
          )}

          {/* Secondary CTA: Tier-aware */}
          <Pressable
            onPress={handleDismiss}
            style={{
              paddingVertical: 12,
              paddingHorizontal: 24,
              minHeight: 44,
            }}
            accessibilityRole="button"
            accessibilityLabel={tierContent.ctaSecondary}
            testID="warning-dismiss-button"
          >
            <Text
              style={{
                color: '#4B5563', // gray-600
                fontSize: 14,
                fontWeight: '500',
                textAlign: 'center',
              }}
            >
              {tierContent.ctaSecondary}
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
